/**
 * The modes themselves.
 *
 * Separate from `mode.ts` so the type definitions and the campaign content can
 * be imported by `entities.ts` without dragging `World` in behind them.
 */

import { CAMPAIGN_CONTENT, EXPEDITION_CONTENT, type ModeId, type ModeRules } from './mode';
import { makeContract, type Contract } from './contracts';
import { hyp } from '../core/math';
import { Rng } from '../core/noise';
import { regionAt } from './level';
import { SurvivalRules } from './survival';
import type { World } from './world';

/** How fast the sub has to be going under nitro before ramming counts. */
const RAM_SPEED = 24;

/**
 * The 7.9 km descent.
 *
 * `tick` is the nine statements that used to sit inline in `World.update`,
 * moved without a single change of behaviour. The *bodies* stay on World: the
 * point of this extraction is to move the decision to run them, not to
 * re-type five working methods and hope the transcription was perfect.
 * `npm run sim` is the check, and it has to report the same numbers.
 */
export const CampaignRules: ModeRules = {
  id: 'campaign',
  label: 'THE DESCENT',
  blurb: '7.9 KM DOWN, ONE WAY TO FIND OUT WHAT IS AT THE BOTTOM',
  content: CAMPAIGN_CONTENT,
  persists: true,

  /**
   * WHERE YOU ARE GOING, AND HOW FAR.
   *
   * The campaign had no standing readout on the reasoning that its instruction
   * is "down" and therefore obvious. It is not: "down" is the shape of the
   * whole game, and the thing a player needs in front of them is the next
   * station and the distance to it. That was already computed — it drove the
   * edge chevron — and then rendered at eight pixels behind an arrow, which is
   * a way of having the information without giving it to anybody.
   */
  status(world: World) {
    const p = world.player;
    const obj = world.nextObjective();
    if (!obj) {
      return {
        title: 'THE HEART',
        headline: `${Math.round(p.depth)} m`,
        headlineLabel: 'DEPTH',
        lines: ['EVERY STATION IS LIT · KEEP GOING DOWN'],
      };
    }
    const dx = obj.x - p.x;
    const d = Math.round(hyp(dx, obj.y - p.y));
    // The arrow is the instruction: a distance with no direction still leaves
    // the player to work out which way, and the answer is not always east.
    const way = dx > 40 ? '►' : dx < -40 ? '◄' : '●';
    const below = obj.y < p.y - 20 ? ' ▼' : obj.y > p.y + 20 ? ' ▲' : '';
    return {
      title: obj.label,
      headline: `${d} m`,
      headlineLabel: `${way}${below}  NEXT STATION`,
      lines: [`${regionAt(p.x).name} · ${Math.round(p.depth)} m DOWN`],
    };
  },

  tick(world: World) {
    const p = world.player;
    world.discoveries();
    world.hints();
    world.stationCheck();
    if (world.lockProgress >= 1) world.eventHint('lock');
    if (p.shieldBroke) world.eventHint('shield');
    if (world.dockable && world.dockable.active && world.salvage > 120) world.eventHint('station');
    if (p.hull < p.stats.maxHull * 0.3) world.eventHint('hull');
    if (p.nitroOn && p.speed > RAM_SPEED) world.eventHint('ram');
    world.checkFinale();
  },
};

/**
 * EXPEDITION: the same water, run as a job.
 *
 * The campaign gives the cargo system four crates in 7.9 km and then never asks
 * again, which is a waste of the most physical verb in the game — the winch,
 * the load factor, the line that fights you. This is that verb as the loop:
 * a contract names a thing, a place and a station, and the whole mode is going
 * out and bringing it back.
 *
 * `persists` is TRUE, unlike Survival. Expedition is not a run to be sealed off
 * — it is a way to earn, in the world you already own boats in, so salvage,
 * upgrades and the fleet all behave exactly as they do in the campaign. What it
 * does not have is an ending: `content.finale` is null, so `checkFinale` has
 * nothing to fire and the player leaves when they want to.
 */
const expedition = {
  contract: null as Contract | null,
  done: 0,
  earned: 0,
  rng: new Rng(0x0EA7),
};

export function expeditionState() { return expedition; }

/** Put the contract's cargo in the water and point the player at it. */
function issue(world: World) {
  const c = makeContract(expedition.done, expedition.rng);
  expedition.contract = c;
  world.actors.spawnLoot(c.x, c.y, c.type, c.amount, 0, 0, {
    mass: c.mass, id: c.id, label: c.label,
  });
  world.showToast(
    `CONTRACT — ${c.label}`,
    `${c.region}  ·  ${c.mass.toFixed(1)} T  ·  DELIVER TO ${c.toName}`,
    '#8fd9ff',
  );
}

export const ExpeditionRules: ModeRules = {
  id: 'expedition',
  label: 'EXPEDITION',
  blurb: 'TAKE A CONTRACT, BRING IT BACK, GET PAID',
  content: EXPEDITION_CONTENT,
  persists: true,

  begin(world: World) {
    expedition.contract = null;
    expedition.done = 0;
    expedition.earned = 0;
    expedition.rng = new Rng(0x0EA7);
    issue(world);
  },

  tick(world: World) {
    // The campaign's ambient statements still apply — this is the same water,
    // and a mode that dropped the hints and the station lights would feel like
    // a worse version of it. `checkFinale` is the one that does not: there is
    // no finale in this content, so it is simply not called.
    const p = world.player;
    world.discoveries();
    world.hints();
    world.stationCheck();
    if (world.lockProgress >= 1) world.eventHint('lock');
    if (p.shieldBroke) world.eventHint('shield');
    if (world.dockable && world.dockable.active && world.salvage > 120) world.eventHint('station');
    if (p.hull < p.stats.maxHull * 0.3) world.eventHint('hull');
    if (p.nitroOn && p.speed > RAM_SPEED) world.eventHint('ram');
  },

  /**
   * Point at the crate until it is on the line, then at the station it is going
   * to. Two halves of one job, and the player should never have to remember
   * which half they are in.
   */
  objective(world: World) {
    const c = expedition.contract;
    if (!c) return null;
    const towing = world.tow.load;
    if (towing && towing.id === c.id) {
      const s = world.rules.content.stations.find((q) => q.id === c.toStation);
      if (s) return { x: s.x, y: s.y, label: `DELIVER — ${c.toName}` };
    }
    return { x: c.x, y: c.y, label: `${c.label} — ${c.region}` };
  },

  /**
   * The standing job card. On screen for as long as the contract is open,
   * because a toast that has faded is the same as never having been told.
   */
  status(world: World) {
    const c = expedition.contract;
    if (!c) return null;
    const towing = world.tow.load?.id === c.id;
    const px = world.player.x;
    const away = Math.round(Math.abs((towing ? c.toX : c.x) - px));
    // Same shape as the campaign's: the distance is the number the player is
    // actually acting on, so it gets the big type and the rest is context.
    const dx = (towing ? c.toX : c.x) - px;
    const way = dx > 40 ? '►' : dx < -40 ? '◄' : '●';
    return {
      title: towing ? `DELIVER TO ${c.toName}` : `RECOVER ${c.label}`,
      headline: `${away} m`,
      headlineLabel: `${way}  CONTRACT ${expedition.done + 1}`,
      lines: towing
        ? [`${c.label} ON THE LINE  ·  ${c.mass.toFixed(1)} T`,
           `PAYS ${c.amount} + BONUS`]
        : [`${c.region}  ·  ${c.mass.toFixed(1)} T`,
           `DELIVER TO ${c.toName}  ·  PAYS ${c.amount} + BONUS`],
    };
  },

  /**
   * Settle the contract. The station already paid for the cargo at face value
   * plus its haul premium; the contract bonus on top is what makes taking the
   * JOB worth more than happening to find the same crate.
   */
  onDeliver(world: World, id: string, paid: number) {
    const c = expedition.contract;
    if (!c || id !== c.id) return;
    if (world.dockable?.id !== c.toStation) {
      // Sold it somewhere else. That is allowed — it is your crate — but it is
      // not the contract, so the job stays open and the crate is gone with it.
      world.showToast('CONTRACT FAILED', `${c.label} SOLD OFF-BOOK`, '#ff5a4a');
      expedition.done++;
      issue(world);
      return;
    }
    const bonus = Math.round(c.amount * 0.6);
    world.purse(bonus);
    expedition.earned += paid + bonus;
    expedition.done++;
    world.showToast('CONTRACT COMPLETE', `+${bonus} BONUS  ·  ${expedition.done} DELIVERED`, '#8fd9ff');
    issue(world);
  },
};

/**
 * The modes that exist. Survival is deliberately absent rather than aliased to
 * the campaign: a placeholder in here puts a second THE DESCENT on the mode
 * card, which is worse than no card at all. It appears when it is real.
 */
export const MODES: Partial<Record<ModeId, ModeRules>> = {
  campaign: CampaignRules,
  expedition: ExpeditionRules,
  survival: SurvivalRules,
};

export const MODE_LIST: readonly ModeRules[] = Object.values(MODES) as ModeRules[];
